import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { X, Cookie } from "lucide-react";
import { loadConsent, saveConsent, applyConsent, DEFAULT_CONSENT } from "@/lib/analytics";

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, ads: false });

  useEffect(() => {
    const existing = loadConsent();
    if (!existing) {
      setVisible(true);
    } else {
      applyConsent({
        ad_storage: existing.ad_storage,
        ad_user_data: existing.ad_user_data,
        ad_personalization: existing.ad_personalization,
        analytics_storage: existing.analytics_storage,
      });
    }
    const reopen = () => {
      const c = loadConsent();
      setPrefs({
        analytics: c?.analytics_storage === "granted",
        ads: c?.ad_storage === "granted",
      });
      setCustomize(true);
      setVisible(true);
    };
    window.addEventListener("re:open-cookie-preferences", reopen);
    return () => window.removeEventListener("re:open-cookie-preferences", reopen);
  }, []);

  const acceptAll = () => {
    const c = {
      ad_storage: "granted",
      ad_user_data: "granted",
      ad_personalization: "granted",
      analytics_storage: "granted",
    };
    saveConsent(c);
    applyConsent(c);
    setVisible(false);
  };

  const rejectAll = () => {
    const c = {
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      analytics_storage: "denied",
    };
    saveConsent(c);
    applyConsent(c);
    setVisible(false);
  };

  const savePrefs = () => {
    const c = {
      ad_storage: prefs.ads ? "granted" : "denied",
      ad_user_data: prefs.ads ? "granted" : "denied",
      ad_personalization: prefs.ads ? "granted" : "denied",
      analytics_storage: prefs.analytics ? "granted" : "denied",
    };
    saveConsent(c);
    applyConsent(c);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[9999] p-3 sm:p-4" role="dialog" aria-labelledby="cookie-title" data-testid="cookie-consent">
      <div className="mx-auto max-w-4xl bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 rounded-xl shadow-2xl border border-neutral-200 dark:border-neutral-800 overflow-hidden">
        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-3 mb-3">
            <Cookie className="h-5 w-5 text-primary mt-0.5 shrink-0" aria-hidden="true" />
            <div className="flex-1">
              <h2 id="cookie-title" className="text-base font-semibold">Confidentialité et cookies</h2>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                Nous utilisons des cookies techniques essentiels au fonctionnement du site. Avec votre accord, nous
                mesurons l'audience et améliorons nos campagnes. Vous pouvez modifier votre choix à tout moment depuis
                la page <a href="/cookies" className="underline">Cookies</a>.
              </p>
            </div>
          </div>

          {customize && (
            <div className="mt-4 space-y-2 rounded-lg border border-border p-4 bg-secondary/40">
              <label className="flex items-center gap-3 cursor-not-allowed opacity-70">
                <input type="checkbox" checked disabled className="h-4 w-4" />
                <span className="text-sm"><strong>Techniques</strong> — nécessaires (toujours actifs)</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer" data-testid="consent-analytics-toggle">
                <input
                  type="checkbox"
                  checked={prefs.analytics}
                  onChange={(e) => setPrefs({ ...prefs, analytics: e.target.checked })}
                  className="h-4 w-4"
                />
                <span className="text-sm"><strong>Mesure d'audience</strong> — Google Analytics</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer" data-testid="consent-ads-toggle">
                <input
                  type="checkbox"
                  checked={prefs.ads}
                  onChange={(e) => setPrefs({ ...prefs, ads: e.target.checked })}
                  className="h-4 w-4"
                />
                <span className="text-sm"><strong>Marketing</strong> — publicités personnalisées</span>
              </label>
            </div>
          )}

          <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:gap-3">
            {customize ? (
              <>
                <Button onClick={savePrefs} className="bg-gradient-brand text-white border-0 hover:opacity-90 w-full sm:flex-1" data-testid="cookie-save-btn">
                  Enregistrer mes choix
                </Button>
                <Button variant="outline" onClick={() => setCustomize(false)} className="w-full sm:w-auto" data-testid="cookie-back-btn">
                  Retour
                </Button>
              </>
            ) : (
              <>
                <Button onClick={rejectAll} variant="outline" className="w-full sm:flex-1 order-2 sm:order-1" data-testid="cookie-reject-btn">
                  Tout refuser
                </Button>
                <Button onClick={() => setCustomize(true)} variant="outline" className="w-full sm:flex-1 order-3 sm:order-2" data-testid="cookie-customize-btn">
                  Personnaliser
                </Button>
                <Button onClick={acceptAll} className="bg-gradient-brand text-white border-0 hover:opacity-90 w-full sm:flex-1 order-1 sm:order-3" data-testid="cookie-accept-btn">
                  Tout accepter
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
